import { useEffect, useMemo, type ComponentProps, type FC } from 'react';

import moment, { type Moment } from 'moment';
import { useIntl } from 'react-intl';

import { DatePicker, Drawer, Form, Input } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import type { IWorkJourneyMatrixUser } from '@src/models/interfaces/workJourneyManagement.interface';
import IntlMessages from '@src/util/IntlMessages';

import { formatMatrixExceptionRangeLabel } from '../../utils/formatMatrixExceptionRangeLabel';
import type { MatrixExceptionDateRange } from '../../utils/resolveMatrixExceptionDateRanges';
import drawerStyles from '../matrixSelectionDrawers.module.less';

const CLARIFICATIONS_MAX = 100;

export type WorkJourneyMatrixVacationDrawerProps = {
  open: boolean;
  user: IWorkJourneyMatrixUser | null;
  initialStartDate: string;
  initialEndDate: string;
  /**
   * Hides editable period controls when the matrix selection is gapped
   * (multiple vacation exceptions). Pass `selectionRanges` to list them.
   */
  hidePeriod?: boolean;
  selectionRanges?: MatrixExceptionDateRange[];
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: {
    startDate: string;
    endDate: string;
    description: string;
  }) => Promise<void> | void;
};

type VacationFormValues = {
  startDate: Moment | null;
  endDate: Moment | null;
  description: string;
};

export const WorkJourneyMatrixVacationDrawer: FC<WorkJourneyMatrixVacationDrawerProps> = ({
  open,
  user,
  initialStartDate,
  initialEndDate,
  hidePeriod = false,
  selectionRanges,
  submitting = false,
  onClose,
  onSubmit,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm<VacationFormValues>();
  const description = Form.useWatch('description', form) ?? '';
  const locale = intl.locale === 'es' ? 'es-CL' : 'en-US';
  const showSelectionRanges = Boolean(hidePeriod && selectionRanges && selectionRanges.length > 0);

  const title = useMemo(
    () =>
      intl.formatMessage(
        { id: 'workJourney.management.matrix.vacationDrawer.title' },
        { name: user?.displayName ?? '—' },
      ),
    [intl, user?.displayName],
  );

  useEffect(() => {
    if (!open) return;
    const start = moment(initialStartDate, 'YYYY-MM-DD');
    const end = moment(initialEndDate, 'YYYY-MM-DD');
    form.setFieldsValue({
      startDate: start.isValid() ? start : null,
      endDate: end.isValid() ? end : null,
      description: '',
    });
  }, [form, initialEndDate, initialStartDate, open]);

  const handleFinish = async (values: VacationFormValues) => {
    const startDate = values.startDate?.format('YYYY-MM-DD');
    const endDate = values.endDate?.format('YYYY-MM-DD');
    if (!startDate || !endDate) return;
    await onSubmit({
      startDate,
      endDate,
      description: (values.description || '').trim().slice(0, CLARIFICATIONS_MAX),
    });
  };

  return (
    <Drawer
      open={open}
      width={560}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={onClose}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            goBack={onClose}
            title={<Title.Header value={title} />}
            actionButtons={[
              {
                label: intl.formatMessage({
                  id: 'workJourney.management.matrix.vacationDrawer.register',
                }),
                type: 'primary',
                action: () => form.submit(),
                disabled: submitting || !user,
              },
            ]}
          />
        </BoxContainer>

        <BoxContainer content>
          <div className={drawerStyles.infoCard}>
            <span className={drawerStyles.infoRowLabel}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.user" />
            </span>
            <span className={drawerStyles.infoRowValue}>{user?.displayName || '—'}</span>
            <span className={drawerStyles.infoRowLabel}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.idCard" />
            </span>
            <span className={drawerStyles.infoRowValue}>{user?.idCard || '—'}</span>
            <span className={drawerStyles.infoRowLabel}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.marking" />
            </span>
            <span className={drawerStyles.infoRowValue}>
              <IntlMessages id="workJourney.management.matrix.vacationDrawer.markingVacation" />
            </span>
          </div>

          <Form
            form={form}
            layout={FORM_LAYOUT_DEFAULT as unknown as ComponentProps<typeof Form>['layout']}
            onFinish={handleFinish}
          >
            {!hidePeriod && (
              <>
                <p className={drawerStyles.sectionTitle}>
                  <IntlMessages id="workJourney.management.matrix.vacationDrawer.periodSection" />
                </p>
                <div className={drawerStyles.dateRow}>
                  <Form.Item
                    name="startDate"
                    label={intl.formatMessage({
                      id: 'workJourney.management.matrix.absenceDrawer.startDate',
                    })}
                    rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
                  >
                    <DatePicker className="gx-w-100" format="DD/MM/YY" />
                  </Form.Item>
                  <Form.Item
                    name="endDate"
                    label={intl.formatMessage({
                      id: 'workJourney.management.matrix.absenceDrawer.endDate',
                    })}
                    rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
                  >
                    <DatePicker className="gx-w-100" format="DD/MM/YY" />
                  </Form.Item>
                </div>
              </>
            )}
            {hidePeriod && (
              <>
                {showSelectionRanges && (
                  <>
                    <p className={drawerStyles.sectionTitle}>
                      <IntlMessages id="workJourney.management.matrix.absenceDrawer.periodsSection" />
                    </p>
                    <ul className={drawerStyles.selectionRanges}>
                      {selectionRanges!.map(range => (
                        <li
                          key={`${range.startDate}_${range.endDate}`}
                          className={drawerStyles.selectionRangeChip}
                        >
                          {formatMatrixExceptionRangeLabel(range, locale)}
                        </li>
                      ))}
                    </ul>
                    <p className={drawerStyles.selectionRangesHint}>
                      <IntlMessages
                        id="workJourney.management.matrix.absenceDrawer.multipleExceptionsHint"
                        values={{ count: selectionRanges!.length }}
                      />
                    </p>
                  </>
                )}
                <Form.Item name="startDate" hidden>
                  <DatePicker />
                </Form.Item>
                <Form.Item name="endDate" hidden>
                  <DatePicker />
                </Form.Item>
              </>
            )}

            <p className={drawerStyles.sectionTitle}>
              <IntlMessages id="workJourney.management.matrix.absenceDrawer.clarifications" />
            </p>
            <div className={drawerStyles.clarificationsWrap}>
              <Form.Item name="description" className="gx-mb-0">
                <Input.TextArea
                  rows={4}
                  maxLength={CLARIFICATIONS_MAX}
                  placeholder={intl.formatMessage({
                    id: 'workJourney.management.matrix.absenceDrawer.clarificationsPlaceholder',
                  })}
                />
              </Form.Item>
              <span className={drawerStyles.charCount}>
                {String(description).length} / {CLARIFICATIONS_MAX}
              </span>
            </div>
          </Form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
